/**
 * Roadmap progress.
 *
 * Kept on the device for the same reason saved roadmaps are: a roadmap is
 * reproducible from two canonical facts plus hand-written copy, so progress is
 * just the learner's own record of what they've done. Stored as an append-only
 * event log ({roadmapId, milestoneId, doneAt}) so "completion over time" is a
 * read over the log rather than a second source of truth.
 */
import type { RoadmapKind } from "@/features/roadmap/roadmaps";
import type { Archetype } from "@/features/assessment-v2/archetypes";
import type { Band } from "@/features/assessment-v2/band";
import { roadmapId } from "@/lib/savedRoadmaps";
import { cloudStorage } from "@/lib/cloudStore";

const KEY = "wcc360.roadmap_progress";

export interface MilestoneDone {
  roadmapId: string;
  milestoneId: string;
  /** ISO timestamp of the moment it was marked done. */
  doneAt: string;
}

export function readProgress(): MilestoneDone[] {
  try {
    const raw = cloudStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? (arr as MilestoneDone[]) : [];
  } catch {
    return [];
  }
}

function write(list: MilestoneDone[]): void {
  try {
    cloudStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage full or unavailable — the UI stays usable */
  }
}

export function progressKey(kind: RoadmapKind, archetype: Archetype, band: Band): string {
  return roadmapId(kind, archetype, band);
}

/** The set of milestone ids marked done for one roadmap. */
export function doneMilestones(key: string): string[] {
  return readProgress()
    .filter((e) => e.roadmapId === key)
    .map((e) => e.milestoneId);
}

export function isMilestoneDone(key: string, milestoneId: string): boolean {
  return readProgress().some((e) => e.roadmapId === key && e.milestoneId === milestoneId);
}

export function markMilestone(
  key: string,
  milestoneId: string,
  done: boolean,
): MilestoneDone[] {
  const rest = readProgress().filter(
    (e) => !(e.roadmapId === key && e.milestoneId === milestoneId),
  );
  const next = done
    ? [...rest, { roadmapId: key, milestoneId, doneAt: new Date().toISOString() }]
    : rest;
  write(next);
  return next;
}

export function clearRoadmapProgress(key: string): MilestoneDone[] {
  const next = readProgress().filter((e) => e.roadmapId !== key);
  write(next);
  return next;
}

export interface ProgressSummary {
  done: number;
  total: number;
  /** 0–100, rounded. Presentation only. */
  percent: number;
  /** ISO timestamp of the most recent step, if any. */
  lastDoneAt: string | null;
}

export function summarise(key: string, total: number): ProgressSummary {
  const events = readProgress().filter((e) => e.roadmapId === key);
  const done = Math.min(events.length, total);
  const lastDoneAt =
    events.length > 0
      ? events.map((e) => e.doneAt).sort().slice(-1)[0]
      : null;
  return {
    done,
    total,
    percent: total > 0 ? Math.round((done / total) * 100) : 0,
    lastDoneAt,
  };
}

export interface DayCount {
  /** YYYY-MM-DD */
  day: string;
  count: number;
}

/**
 * Steps completed per day over the last `days` days, oldest first. Every day is
 * present (count 0 when nothing happened) so a chart never has holes.
 */
export function completionByDay(key: string | null, days = 14): DayCount[] {
  const events = readProgress().filter((e) => (key ? e.roadmapId === key : true));
  const counts = new Map<string, number>();
  for (const e of events) {
    const day = e.doneAt.slice(0, 10);
    counts.set(day, (counts.get(day) ?? 0) + 1);
  }
  const out: DayCount[] = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const day = d.toISOString().slice(0, 10);
    out.push({ day, count: counts.get(day) ?? 0 });
  }
  return out;
}

/** Consecutive days up to today with at least one step done. */
export function currentStreak(key: string | null): number {
  const byDay = completionByDay(key, 60);
  let streak = 0;
  for (let i = byDay.length - 1; i >= 0; i -= 1) {
    if (byDay[i].count > 0) streak += 1;
    else break;
  }
  return streak;
}

/* ------------------------------------------------------------------ *
 * Time spent
 *
 * Also an append-only log: each entry is a stretch of effort against one
 * milestone. Totals are a read over the log, so nothing can drift.
 * ------------------------------------------------------------------ */

const TIME_KEY = "wcc360.roadmap_time";

export interface TimeEntry {
  /** Stable id so two devices can merge their logs without duplicating. */
  id: string;
  roadmapId: string;
  milestoneId: string;
  seconds: number;
  /** ISO timestamp of when the stretch ended. */
  at: string;
}

export function readTimeLog(): TimeEntry[] {
  try {
    const raw = cloudStorage.getItem(TIME_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? (arr as TimeEntry[]) : [];
  } catch {
    return [];
  }
}

export function writeTimeLog(list: TimeEntry[]): void {
  try {
    cloudStorage.setItem(TIME_KEY, JSON.stringify(list));
  } catch {
    /* storage full or unavailable — the UI stays usable */
  }
}

export function writeProgressLog(list: MilestoneDone[]): void {
  write(list);
}

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
}

/** Records a stretch of effort. Anything under 5 seconds is ignored as a mis-tap. */
export function logTime(key: string, milestoneId: string, seconds: number): TimeEntry[] {
  const rounded = Math.round(seconds);
  if (rounded < 5) return readTimeLog();
  const next = [
    ...readTimeLog(),
    { id: newId(), roadmapId: key, milestoneId, seconds: rounded, at: new Date().toISOString() },
  ];
  writeTimeLog(next);
  return next;
}

/** Seconds logged per milestone id for one roadmap. */
export function secondsByMilestone(key: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of readTimeLog()) {
    if (e.roadmapId !== key) continue;
    out[e.milestoneId] = (out[e.milestoneId] ?? 0) + e.seconds;
  }
  return out;
}

export function totalSeconds(key: string): number {
  return readTimeLog()
    .filter((e) => e.roadmapId === key)
    .reduce((sum, e) => sum + e.seconds, 0);
}

export function clearRoadmapTime(key: string): TimeEntry[] {
  const next = readTimeLog().filter((e) => e.roadmapId !== key);
  writeTimeLog(next);
  return next;
}

/** "25 min", "1 h 05", "—" — short enough to sit inside a row. */
export function formatDuration(seconds: number): string {
  if (!seconds) return "—";
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

/** Monday-start ISO date of the week containing `d`. */
export function weekStart(d = new Date()): string {
  const copy = new Date(d);
  const day = (copy.getDay() + 6) % 7; // Monday = 0
  copy.setDate(copy.getDate() - day);
  return copy.toISOString().slice(0, 10);
}

/** Steps marked done this week, and minutes logged this week. */
export function thisWeek(key: string): { steps: number; minutes: number } {
  const start = weekStart();
  const steps = readProgress().filter(
    (e) => e.roadmapId === key && e.doneAt.slice(0, 10) >= start,
  ).length;
  const seconds = readTimeLog()
    .filter((e) => e.roadmapId === key && e.at.slice(0, 10) >= start)
    .reduce((s, e) => s + e.seconds, 0);
  return { steps, minutes: Math.round(seconds / 60) };
}
